import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Grid3x3, Sparkles, Inbox } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import PostsPanel from "./panels/PostsPanel";
import HighlightsPanel from "./panels/HighlightsPanel";
import SubmissionsPanel from "./panels/SubmissionsPanel";

const TABS = [
  { key: "posts", label: "Posts", icon: Grid3x3 },
  { key: "highlights", label: "Highlights", icon: Sparkles },
  { key: "submissions", label: "Submissions", icon: Inbox },
];

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  const [tab, setTab] = useState("posts");

  return (
    <div className="min-h-screen bg-[#4A0E1F] text-[#FDFBF7]">
      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-[#D4AF37]/25 bg-fl-glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10">
          <div className="flex items-center gap-4">
            <a href="/" className="font-serif text-2xl italic text-[#D4AF37] hover:text-[#F5D97A] transition-colors">
              Fairy Lights
            </a>
            <span className="hidden font-body text-[11px] uppercase tracking-[0.28em] text-[#FDFBF7]/50 md:inline">
              · The Atelier
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-body text-[13px] text-[#FDFBF7]/70 md:inline">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="pill pill-ghost"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10 sm:py-14">
        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
            The Studio · Content Management
          </span>
        </div>
        <h1 className="mb-10 font-serif text-5xl leading-none tracking-tight md:text-6xl">
          Compose the profile.
        </h1>

        {/* Tabs */}
        <div className="mb-10 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`pill ${active ? "pill-primary" : "pill-ghost"}`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {tab === "posts" && <PostsPanel />}
            {tab === "highlights" && <HighlightsPanel />}
            {tab === "submissions" && <SubmissionsPanel />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
